import React, { useEffect, useState } from "react";
import axios from "axios";
import { baseUrl } from "../../../helper/urls";
import { useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import MasterLayout from "../../../masterLayout/MasterLayout";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Icon } from "@iconify/react/dist/iconify.js";
import AdminCustomTable from "../../../components/custom_table/AdminCustomTable";
import { Box, Tooltip } from "@mui/material";

export default function StudentSidebarContent() {
  const { token } = useSelector((state) => state.auth);

  const allowedFields = ["story", "highlight", "webinar", "statement"];

  // ──────────────────────────────────── State ────────────────────────────────────
  const [category, setCategory] = useState("");
  const [content, setContent] = useState("");
  const [date, setDate] = useState(""); // YYYY‑MM‑DD for <input type="date">
  const [timeFrom, setTimeFrom] = useState(""); // HH:mm   for <input type="time">
  const [timeTo, setTimeTo] = useState("");
  const [meetLink, setMeetLink] = useState("");

  const [contentDataList, setContentDataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [id, setId] = useState("");

  // ──────────────────────────────────── Helpers ───────────────────────────────────
  const resetForm = () => {
    setCategory("");
    setContent("");
    setDate("");
    setTimeFrom("");
    setTimeTo("");
    setMeetLink("");
    setIsEdit(false);
    setId("");
  };

  /** "2025-07-27" -> "Sunday, July 27th 2025" */
  const formatDateForApi = (isoDate) => {
    const d = new Date(isoDate);
    if (isNaN(d)) return "";
    const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
    const month = d.toLocaleDateString("en-US", { month: "long" });
    const day = d.getDate();
    const year = d.getFullYear();
    const ordinal = (n) =>
      n > 3 && n < 21 ? "th" : ["st", "nd", "rd"][(n % 10) - 1] || "th";
    return `${weekday}, ${month} ${day}${ordinal(day)} ${year}`;
  };

  /** "14:30" -> "02:30 PM" */
  const toAmPm = (hm) => {
    if (!hm) return "";
    const [h, m] = hm.split(":");
    const d = new Date();
    d.setHours(+h, +m);
    return d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /** "02:30 PM" -> "14:30" */
  const to24Hour = (ampm) => {
    const [time, mod] = ampm.trim().split(" ");
    if (!time || !mod) return "";
    let [h, m] = time.split(":").map(Number);
    if (mod === "PM" && h !== 12) h += 12;
    if (mod === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  /** e.g. "02:00 PM - 03:30 PM" */
  const buildTimeForApi = () => `${toAmPm(timeFrom)} - ${toAmPm(timeTo)}`;

  /** "Sunday, July 27th 2025" -> "2025-07-27" (for editing) */
  const apiDateToIso = (pretty) => {
    const d = new Date(pretty);
    return isNaN(d)
      ? ""
      : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // ───────────────────────────────── API Calls ───────────────────────────────────
  const fetchList = async () => {
    try {
      const res = await axios.get(`${baseUrl}/api/policy/content/getAll`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContentDataList(res.data.data || []);
    } catch {
      toast.error("Failed to fetch content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const createOrUpdate = async ({ update = false, e }) => {
    e.preventDefault();
    if (!category || !content) {
      toast.error("Category and content are required");
      return;
    }
    if (
      category === "webinar" &&
      (!date || !timeFrom || !timeTo || !meetLink)
    ) {
      toast.error("Please complete all webinar fields");
      return;
    }

    const payload =
      category === "webinar"
        ? {
            category,
            content,
            date: formatDateForApi(date),
            time: buildTimeForApi(),
            meet_link: meetLink,
          }
        : { category, content };

    try {
      update ? setEditLoading(true) : setSubmitLoading(true);
      const url = update
        ? `${baseUrl}/api/policy/content/update/${id}`
        : `${baseUrl}/api/policy/content/create`;
      await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(update ? "Content updated" : "Content created");
      fetchList();
      resetForm();
    } catch {
      toast.error(update ? "Update failed" : "Create failed");
    } finally {
      update ? setEditLoading(false) : setSubmitLoading(false);
    }
  };

  const handleDelete = async (rowId) => {
    const ok = await Swal.fire({
      html: `<div style="font-size: 30px;">Delete this content?</div>`,
      text: "You won't be able to revert!",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${baseUrl}/api/policy/content/destroy/${rowId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      fetchList();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleToggleStatus = async (rowId, currentStatus) => {
    const action = currentStatus === 1 ? "Hide" : "Show";
    const ok = await Swal.fire({
      html: `<div style="font-size: 30px;">${action} this content?</div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.post(
        `${baseUrl}/api/policy/content/toggle/${rowId}`,
        { status: currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Content ${action === "Hide" ? "hidden" : "visible"}`);
      fetchList();
    } catch {
      toast.error("Status change failed");
    }
  };

  // ───────────────────────────── Table Column Config ─────────────────────────────
  const columns = [
    {
      header: "Actions",
      Cell: ({ row }) => {
        const data = row.original;
        return (
          <Box>
            <Tooltip>
              <button
                onClick={() => {
                  setId(data.id);
                  setCategory(data.category);
                  setContent(data.content);
                  if (data.category === "webinar") {
                    setDate(apiDateToIso(data.date));
                    const [fromRaw, toRaw] = data.time
                      .split("-")
                      .map((s) => s.trim());
                    setTimeFrom(to24Hour(fromRaw));
                    setTimeTo(to24Hour(toRaw));
                    setMeetLink(data.meet_link || "");
                  } else {
                    setDate("");
                    setTimeFrom("");
                    setTimeTo("");
                    setMeetLink("");
                  }
                  setIsEdit(true);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="w-32-px h-32-px me-8  pl-2 bg-info-focus text-info-main rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
              >
                <Icon icon="lucide:edit" />
              </button>
            </Tooltip>
            <Tooltip title="Hide / Unhide" arrow>
              <button
                onClick={() => handleToggleStatus(data.id, data.status)}
                className="w-32-px h-32-px me-8 bg-success-focus rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                style={{ cursor: "pointer" }}
              >
                {data.status === 1 ? (
                  <Icon
                    icon="mingcute:eye-line"
                    style={{ color: "#28a745", fontSize: "20px" }}
                  />
                ) : (
                  <Icon
                    icon="ep:hide"
                    style={{ color: "#28a745", fontSize: "20px" }}
                  />
                )}
              </button>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <button
                onClick={() => handleDelete(data.id)}
                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                style={{ cursor: "pointer" }}
              >
                <Icon icon="mingcute:delete-2-line" />
              </button>
            </Tooltip>
          </Box>
        );
      },
    },
    { header: "Category", accessorKey: "category" },
    {
      header: "Content",
      accessorKey: "content",
      Cell: ({ cell }) => {
        const txt = cell.getValue() || "-";
        return txt.length > 50 ? `${txt.slice(0, 50)}…` : txt;
      },
    },
    {
      header: "Date",
      accessorKey: "date",
      Cell: ({ row }) =>
        row.original.category === "webinar" ? row.original.date || "-" : "-",
    },
    {
      header: "Time",
      accessorKey: "time",
      Cell: ({ row }) =>
        row.original.category === "webinar" ? row.original.time || "-" : "-",
    },
    {
      header: "Meet Link",
      accessorKey: "meet_link",
      Cell: ({ row }) =>
        row.original.category === "webinar" && row.original.meet_link ? (
          <a
            href={row.original.meet_link}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open Link
          </a>
        ) : (
          "-"
        ),
    },
  ];

  // ──────────────────────────────────── JSX ───────────────────────────────────────
  return (
    <>
      <ToastContainer />
      <MasterLayout>
        <div className="card p-16">
          <h5>{isEdit ? "Edit Content" : "Create New Content"}</h5>

          <form
            className="row g-3 mt-2"
            onSubmit={(e) => createOrUpdate({ update: isEdit, e })}
          >
            {/* Category */}
            <div className="col-md-3">
              <label className="form-label fw-bold">Category</label>
              <select
                value={category}
                className="form-select"
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">-- Select --</option>
                {allowedFields.map((f) => (
                  <option key={f} value={f}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Webinar‑only fields */}
            {category === "webinar" && (
              <>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Time From</label>
                  <input
                    type="time"
                    className="form-control"
                    value={timeFrom}
                    onChange={(e) => setTimeFrom(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Time To</label>
                  <input
                    type="time"
                    className="form-control"
                    value={timeTo}
                    onChange={(e) => setTimeTo(e.target.value)}
                  />
                </div>
                <div className="col-md-12">
                  <label className="form-label fw-bold">Meet Link</label>
                  <input
                    type="url"
                    className="form-control"
                    value={meetLink}
                    onChange={(e) => setMeetLink(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Content */}
            <div className="col-md-12">
              <label className="form-label fw-bold">Content</label>
              <textarea
                rows="4"
                className="form-control"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>

            {/* Buttons */}
            <div className="row justify-content-end">
              <div className="col-3 mt-3">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={submitLoading || editLoading}
                >
                  {submitLoading || editLoading
                    ? isEdit
                      ? "Updating…"
                      : "Creating…"
                    : isEdit
                    ? "Update"
                    : "Create"}
                </button>
              </div>
              {isEdit && (
                <div className="col-3 mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary w-100"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Table */}
        <div className="mt-16">
          <AdminCustomTable
            columns={columns}
            data={contentDataList}
            loading={loading}
          />
        </div>
      </MasterLayout>
    </>
  );
}
